import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../auth/AuthContext';
import { useViewContext } from '../context/ViewContext';
import { adminUsersService } from '../api/apiClient';
import CreateUserDialog from '../components/dialogs/CreateUserDialog';
import EditUserDialog from '../components/dialogs/EditUserDialog';
import EditRolesDialog from '../components/dialogs/EditRolesDialog';
import UserDevicesDialog from '../components/dialogs/UserDevicesDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';


/**
 * Step 2 (v1): read-only user list for superusers.
 * - Server-side pagination + filters backed by DBAM /api/admin/users
 * - Explicit guard: non-superusers see Not Authorized
 */
export default function UserManagementView() {
  const auth = useAuth();
  const { setView } = useViewContext();
  const isSuperuser = auth.isSuperuser;

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'active' | 'inactive'

  // Server pagination model
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // Error handling + retry
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [rolesUser, setRolesUser] = useState(null);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [devicesUser, setDevicesUser] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);


  // Prevent race conditions: only apply the latest response
  const requestSeq = useRef(0);

  const queryParams = useMemo(() => {
    const limit = paginationModel.pageSize;
    const offset = paginationModel.page * paginationModel.pageSize;
    const q = debouncedSearchText.trim();
    const roleName = roleFilter || undefined;
    const isActive =
      statusFilter === 'active' ? true :
      statusFilter === 'inactive' ? false :
      undefined;
    return { limit, offset, q: q || undefined, roleName, isActive };
  }, [debouncedSearchText, paginationModel.page, paginationModel.pageSize, roleFilter, statusFilter]);

  // Debounce search input to avoid spamming DBAM while typing.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchText]);

  // When filters change, reset to page 0 (server paging).
  useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [debouncedSearchText, roleFilter, statusFilter]);

  const fetchUsersWithParams = useCallback(async (params) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);

    const resp = await adminUsersService.listUsers(params);

    // Ignore out-of-order responses.
    if (seq !== requestSeq.current) return;

    setLoading(false);

    if (!resp.success) {
      // Pagination recovery rule: keep last-good rows; just surface error + allow retry.
      setError(resp);
      setSnackbar({ open: true, message: resp.message || 'Failed to load users', severity: 'error' });
      if (resp.status === 401) {
        auth.signOut();
      }
      return;
    }

    const data = resp.data?.data || resp.data || [];
    const meta = resp.data?.metadata || {};
    setRows(Array.isArray(data) ? data : []);
    setRowCount(Number.isFinite(meta.totalRecords) ? meta.totalRecords : (Array.isArray(data) ? data.length : 0));
  }, [auth]);

  const fetchUsers = useCallback(async () => {
    return fetchUsersWithParams(queryParams);
  }, [fetchUsersWithParams, queryParams]);

  const handleCreateUser = useCallback(async (payload) => {
    setLoading(true);
    const resp = await adminUsersService.createUser(payload);
    setLoading(false);

    if (!resp.success) {
      const detailsMsg = resp.data?.details?.message || resp.data?.details?.details?.error?.message || '';
      const msg = detailsMsg ? `${resp.message || 'Failed to create user'} (${detailsMsg})` : (resp.message || 'Failed to create user');
      setSnackbar({ open: true, message: msg, severity: 'error' });
      if (resp.status === 401) auth.signOut();
      return { ok: false, message: msg, error: resp.data };
    }

    setSnackbar({ open: true, message: resp.message || 'User created', severity: 'success' });


    // After create, refresh list (and land on page 0 so the new user is visible deterministically).
    setPaginationModel((p) => ({ ...p, page: 0 }));
    // Tighten refresh: explicitly re-fetch page 0 using current filters/pageSize (no reliance on async state updates).
    await fetchUsersWithParams({ ...queryParams, offset: 0, limit: paginationModel.pageSize });
    return { ok: true, data: resp.data };
  }, [auth, fetchUsersWithParams, paginationModel.pageSize, queryParams]);

  const handleSaveUser = useCallback(async (userId, payload) => {
    setLoading(true);
    const resp = await adminUsersService.updateUser(userId, payload);
    setLoading(false);

    if (!resp.success) {
      setSnackbar({ open: true, message: resp.message || 'Failed to update user', severity: 'error' });
      if (resp.status === 401) auth.signOut();
      return { ok: false, message: resp.message, error: resp.data };
    }

    setSnackbar({ open: true, message: resp.message || 'User updated', severity: 'success' });
    await fetchUsers();
    return { ok: true, data: resp.data };
  }, [auth, fetchUsers]);

  const handleSaveRoles = useCallback(async (userId, roles) => {
    setLoading(true);
    const resp = await adminUsersService.replaceUserRoles(userId, roles);
    setLoading(false);

    if (!resp.success) {
      // DBAM last-superuser protection is a 400 with a clear message; show it verbatim.
      setSnackbar({ open: true, message: resp.message || 'Failed to update roles', severity: 'error' });
      if (resp.status === 401) auth.signOut();
      return { ok: false, message: resp.message, error: resp.data };
    }

    setSnackbar({ open: true, message: resp.message || 'Roles updated', severity: 'success' });
    await fetchUsers();
    return { ok: true, data: resp.data };
  }, [auth, fetchUsers]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTargetUser?.userId) return;
    setIsDeleting(true);
    const resp = await adminUsersService.deleteUser(deleteTargetUser.userId);
    setIsDeleting(false);

    if (!resp.success) {
      setSnackbar({ open: true, message: resp.message || 'Failed to delete user', severity: 'error' });
      if (resp.status === 401) auth.signOut();
      return;
    }

    const devicesReset = resp.data?.data?.devicesReset;
    const suffix = Number.isFinite(devicesReset) ? ` (${devicesReset} devices reset)` : '';
    setSnackbar({ open: true, message: `${resp.message || 'User deleted'}${suffix}`, severity: 'success' });

    setDeleteOpen(false);
    setDeleteTargetUser(null);
    await fetchUsers();
  }, [auth, deleteTargetUser, fetchUsers]);

  useEffect(() => {
    if (!isSuperuser) return;
    fetchUsers();
  }, [fetchUsers, isSuperuser]);

  const columns = useMemo(() => ([
    {
      field: '_actions',
      headerName: 'Actions',
      width: 360,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setEditUser(params.row);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setRolesUser(params.row);
              setRolesOpen(true);
            }}
          >
            Roles
          </Button>
          {(params.row.roles || []).includes('sabal_app_user') && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDevicesUser(params.row);
                setDevicesOpen(true);
              }}
            >
              Devices
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              setDeleteTargetUser(params.row);
              setDeleteOpen(true);
            }}
          >
            Delete
          </Button>
        </Stack>
      )
    },
    { field: 'sabalLoginEmail', headerName: 'Login Email', flex: 1, minWidth: 220 },
    { field: 'displayName', headerName: 'Display Name', flex: 1, minWidth: 160, valueGetter: (p) => p.row.displayName || '—' },
    { field: 'contactEmail', headerName: 'Contact Email', flex: 1, minWidth: 200, valueGetter: (p) => p.row.contactEmail || '—' },
    { field: 'customerId', headerName: 'Customer', width: 140, valueGetter: (p) => p.row.customerId || '—' },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const roles = params.row.roles || [];
        if (!roles.length) return '—';
        return (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            {roles.map((r) => <Chip key={r} size="small" label={r} />)}
          </Stack>
        );
      }
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        params.row.isActive
          ? <Chip size="small" color="success" label="Active" />
          : <Chip size="small" color="default" label="Inactive" />
      )
    },
    {
      field: 'lastLogin',
      headerName: 'Last Login',
      width: 180,
      valueGetter: (p) => p.row.lastLogin || null,
      valueFormatter: (p) => {
        if (!p.value) return '—';
        try { return new Date(p.value).toLocaleString(); } catch { return String(p.value); }
      }
    }
  ]), []);

  // Guard: UI is not security, but we explicitly block accidental view access.
  if (!isSuperuser) {
    return (
      <Box>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>User Management</Typography>
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => setView('dashboard')}>Go to Dashboard</Button>}
          >
            Not authorized. This page is available to superusers only.
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">User Management</Typography>
          <Button variant="outlined" onClick={() => setCreateOpen(true)}>
            Create user
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            label="Search"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Email, contact email, or display name"
            sx={{ minWidth: 280, flex: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="">All roles</MenuItem>
              <MenuItem value="someac_superuser">someac_superuser</MenuItem>
              <MenuItem value="someac_owner">someac_owner</MenuItem>
              <MenuItem value="someac_guest">someac_guest</MenuItem>
              <MenuItem value="sabal_app_user">sabal_app_user</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={fetchUsers} disabled={loading}>
            Retry
          </Button>
        </Stack>

        <Box sx={{ height: 560, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.userId}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
            disableRowSelectionOnClick
          />
        </Box>

        {error && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">
              {error.message || 'Failed to load users.'} (showing last successful results)
            </Alert>
          </Box>
        )}
      </Paper>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreateUser}
      />

      <EditUserDialog
        open={editOpen}
        user={editUser}
        onClose={() => {
          setEditOpen(false);
          setEditUser(null);
        }}
        onSave={handleSaveUser}
      />

      <EditRolesDialog
        open={rolesOpen}
        user={rolesUser}
        onClose={() => {
          setRolesOpen(false);
          setRolesUser(null);
        }}
        onSave={handleSaveRoles}
      />

      <UserDevicesDialog
        open={devicesOpen}
        user={devicesUser}
        onClose={() => {
          setDevicesOpen(false);
          setDevicesUser(null);
        }}
        onSnackbar={({ message, severity }) => setSnackbar({ open: true, message, severity: severity || 'info' })}
      />

      <ConfirmDeleteDialog
        open={deleteOpen}
        isDeleting={isDeleting}
        title="Delete User?"
        message={
          deleteTargetUser?.sabalLoginEmail
            ? `Delete ${deleteTargetUser.sabalLoginEmail}? This removes the user account and authentication record. If the user has devices, they will be reset and disabled. This cannot be undone.`
            : 'Delete this user? This cannot be undone.'
        }
        onClose={() => {
          if (isDeleting) return;
          setDeleteOpen(false);
          setDeleteTargetUser(null);
        }}
        onConfirm={handleConfirmDelete}
      />



      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


